import { useTheme } from "@emotion/react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import FormService from "../../services/doctorServices/FormService";
import FormulaService from "../../services/doctorServices/FormulaService";
import DoctorService from "../../services/doctorService";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
  TextField as MuiTextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Radio,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import Headeer from "../../components/Headeer";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import FormGradientButton from "../../components/FormGradientButton";
import TableChartIcon from "@mui/icons-material/TableChart";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import ImageQuestion from "../../components/ImageQuestion";
import BlockIcon from "@mui/icons-material/Block";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RepeatSchedulePicker from "../../components/RepeatSchedulePicker";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import RatingScaleQuestionEditor from "../../components/RatingScaleQuestionEditor";

function tableEqualColumnWidthPct(totalColumns) {
  const raw = Number(totalColumns);
  const n = Number.isFinite(raw) && raw >= 1 ? raw : 1;
  return `${100 / n}%`;
}

function tableCellShrinkBase(totalColumns, extra = {}) {
  return {
    minWidth: 0,
    width: tableEqualColumnWidthPct(totalColumns),
    verticalAlign: "top",
    wordBreak: "break-word",
    boxSizing: "border-box",
    ...extra,
  };
}

export default function FormEdit() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(["forms", "common"]);
  const formService = useMemo(() => new FormService(), []);
  const formulaService = useMemo(() => new FormulaService(), []);
  const doctorService = useMemo(() => new DoctorService(), []);
  const { formId } = useParams();
  const navigate = useNavigate();
  
  // Form States
  const [formInfo, setFormInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasPermission, setHasPermission] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [formulas, setFormulas] = useState([]);
  const [selectedFormulaId, setSelectedFormulaId] = useState("");
  
  // Repeat schedule states (only for "for patients" type)
  const [repeatCount, setRepeatCount] = useState("");
  const [repeatSchedule, setRepeatSchedule] = useState([]);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [formInvitations, setFormInvitations] = useState([]);
  const [maxSentCount, setMaxSentCount] = useState(0); // Maximum count that has been sent
  
  // Doctor info states
  const [currentDoctorId, setCurrentDoctorId] = useState(null);
  const [currentDoctorRole, setCurrentDoctorRole] = useState(null);
  
  // Dialog States
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  
  // Table Question Modal States
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableRows, setTableRows] = useState(2);
  const [tableCols, setTableCols] = useState(2);
  const [tableHeaders, setTableHeaders] = useState(["Option 1", "Option 2"]);
  const [tableQuestions, setTableQuestions] = useState(["Question 1", "Question 2"]);
  const [tableHeaderImportance, setTableHeaderImportance] = useState([0, 0]);

  // Fetch doctor info and formulas
  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        // Try to get from localStorage first (if available)
        const storedDoctorInfo = JSON.parse(localStorage.getItem("doctorInfo") || "{}");
        if (storedDoctorInfo?.doctor?.id && storedDoctorInfo?.doctor?.role) {
          setCurrentDoctorId(storedDoctorInfo.doctor.id);
          setCurrentDoctorRole(storedDoctorInfo.doctor.role);
        } else {
          // If not in localStorage, fetch from API
          const doctorInfo = await doctorService.getDoctorInfo();
          if (doctorInfo) {
            setCurrentDoctorId(doctorInfo.doctor?.id || doctorInfo.id);
            setCurrentDoctorRole(doctorInfo.doctor?.role || doctorInfo.role);
            // Also save to localStorage for future use
            localStorage.setItem("doctorInfo", JSON.stringify(doctorInfo));
          } else {
            // Fallback to user object from localStorage
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            setCurrentDoctorId(user.id);
            setCurrentDoctorRole(user.role);
          }
        }
      } catch (err) {
        console.error("Doktor bilgileri getirilirken hata:", err);
        // Fallback to user object from localStorage
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        setCurrentDoctorId(user.id);
        setCurrentDoctorRole(user.role);
      }
    };
    
    fetchDoctorInfo();
  }, [doctorService]);

  // Fetch formulas
  useEffect(() => {
    const getFormulas = async () => {
      try {
        const response = await formulaService.getAllFormulas();
        setFormulas(response);
      } catch (err) {
        console.error("Formüller getirilirken hata oluştu:", err);
      }
    };
    getFormulas();
  }, [formulaService]);

  useEffect(() => {
    // Wait for doctor info to be loaded before fetching form
    if (!currentDoctorId || !currentDoctorRole) {
      return;
    }

    const fetchFormInfo = async () => {
      try {
        setLoading(true);
        const response = await formService.getFormInfo(formId);

        // Backend 200 döndüyse izin doğrulamasını sunucu zaten yaptı;
        // sadece veri eksikliği durumunda kullanıcıyı geriye yönlendir.
        const file = response.File;
        if (!file) {
          setSnackbar({
            open: true,
            message: "Form file bilgisi bulunamadı",
            severity: "error"
          });
          setTimeout(() => navigate(-1), 2000);
          return;
        }

        setHasPermission(true);
        setFormInfo(response);
        
        // Set selected formula ID if exists
        if (response.scoring_formula_id) {
          setSelectedFormulaId(response.scoring_formula_id);
        }
        
        // Set repeat_count and repeat_schedule if form type is "for patients"
        if (response.type === "for patients") {
          setRepeatCount(response.repeat_count?.toString() || "1");
          setRepeatSchedule(response.repeat_schedule || []);
          
          // Fetch form invitations to check which sends have been completed
          try {
            const invitations = await formService.getFormInvitations(formId);
            setFormInvitations(invitations || []);
            
            // Find the maximum count that has been sent
            const maxCount = invitations && invitations.length > 0
              ? Math.max(...invitations.map(inv => inv.count || 1))
              : 0;
            setMaxSentCount(maxCount);
          } catch (err) {
            console.error("Form invitations getirilirken hata:", err);
            setFormInvitations([]);
            setMaxSentCount(0);
          }
        }
      } catch (error) {
        console.error("Form bilgileri getirilirken hata:", error);
        
        // 403 hatası - Permission error (global alert zaten gösteriliyor)
        if (error.response?.status === 403) {
          setHasPermission(false);
          setTimeout(() => navigate(-1), 3000);
        } else {
          setSnackbar({
            open: true,
            message: error.response?.data?.message || "Form yüklenirken hata oluştu",
            severity: "error"
          });
          setTimeout(() => navigate(-1), 3000);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchFormInfo();
  }, [formId, currentDoctorId, currentDoctorRole, formService, navigate]);
  const handleDialogOpen = (type) => {
    setDialogOpen(true);
    setDialogType(type);
  };
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  const handleCancel = () => {
    navigate(-1);
  };
  const handleSave = async () => {
    // Double-check permission
    if (!hasPermission) {
      setSnackbar({
        open: true,
        message: "Form düzenleme yetkiniz yok",
        severity: "error"
      });
      return;
    }

    try {
      setSaving(true);
      
      // Validate repeat_count and repeat_schedule for "for patients" type
      if (formInfo.type === "for patients") {
        const count = parseInt(repeatCount) || 0;
        
        // Check if repeat_count is less than maxSentCount
        if (count < maxSentCount) {
          setSnackbar({
            open: true,
            message: `Total repetitions cannot be less than ${maxSentCount} because ${maxSentCount} send(s) have already been completed.`,
            severity: "error"
          });
          setSaving(false);
          return;
        }
        
        // Validate repeat schedule if count > 1
        if (count > 1) {
          if (!repeatSchedule || repeatSchedule.length !== count - 1) {
            setSnackbar({
              open: true,
              message: `Please configure the repeat schedule. You need to set ${count - 1} schedule item(s) for ${count} total repetitions.`,
              severity: "error"
            });
            setSaving(false);
            return;
          }
          
          // Validate that all schedule items are filled
          const hasEmptyItems = repeatSchedule.some(item => !item || item === "");
          if (hasEmptyItems) {
            setSnackbar({
              open: true,
              message: "Please fill in all schedule items. Each subsequent send must have a time interval configured.",
              severity: "error"
            });
            setSaving(false);
            return;
          }
        }
      }
      
      // Prepare update data with formula and repeat schedule
      const updateData = {
        ...formInfo,
        scoring_formula_id: selectedFormulaId || null,
      };
      
      // Add repeat_count and repeat_schedule for "for patients" type
      if (formInfo.type === "for patients") {
        updateData.repeat_count = parseInt(repeatCount) || 1;
        updateData.repeat_schedule = (parseInt(repeatCount) > 1 && repeatSchedule && repeatSchedule.length > 0) 
          ? repeatSchedule 
          : null;
      }
      
      const response = await formService.updateForm(formId, updateData);
      console.log("Form başarıyla güncellendi: ", response);
      
      setSnackbar({
        open: true,
        message: "Form başarıyla güncellendi!",
        severity: "success"
      });
      
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      console.error("Form güncellenirken hata:", error);
      
      // 403 hatası - Permission error (global alert zaten gösteriliyor)
      if (error.response?.status === 403) {
        setTimeout(() => navigate(-1), 2000);
      } else {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Form güncellenirken hata oluştu",
          severity: "error"
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Table Question Functions
  const handleAddTableQuestion = () => {
    setTableModalOpen(true);
    setTableCols(2);
    setTableHeaders(["Option 1", "Option 2"]);
    setTableQuestions(["Question 1", "Question 2"]);
    setTableHeaderImportance([0, 0]);
  };

  const handleTableRowsChange = (newRows) => {
    if (newRows >= 2 && newRows <= 20) {
      setTableRows(newRows);
      const newQuestions = [...tableQuestions];
      if (newRows > tableQuestions.length) {
        for (let i = tableQuestions.length; i < newRows; i++) {
          newQuestions.push(`Question ${i + 1}`);
        }
      } else {
        newQuestions.splice(newRows);
      }
      setTableQuestions(newQuestions);
    }
  };

  const handleTableColsChange = (newCols) => {
    if (newCols >= 2 && newCols <= 15) {
      setTableCols(newCols);
      const newHeaders = [...tableHeaders];
      const newImportance = [...tableHeaderImportance];
      
      if (newCols > tableHeaders.length) {
        for (let i = tableHeaders.length; i < newCols; i++) {
          newHeaders.push(`Option ${i + 1}`);
          newImportance.push(0);
        }
      } else {
        newHeaders.splice(newCols);
        newImportance.splice(newCols);
      }
      setTableHeaders(newHeaders);
      setTableHeaderImportance(newImportance);
    }
  };

  const handleTableHeaderChange = (index, value) => {
    const newHeaders = [...tableHeaders];
    newHeaders[index] = value;
    setTableHeaders(newHeaders);
  };

  const handleTableQuestionChange = (index, value) => {
    const newQuestions = [...tableQuestions];
    newQuestions[index] = value;
    setTableQuestions(newQuestions);
  };

  const handleTableHeaderImportanceChange = (index, value) => {
    const newImportance = [...tableHeaderImportance];
    newImportance[index] = value === "" ? 0 : Number(value);
    setTableHeaderImportance(newImportance);
  };

  const handleCreateTableQuestion = () => {
    const newQuestion = {
      type: "table",
      question: "Table Question",
      tableRows: tableRows,
      tableCols: tableCols,
      tableHeaders: [...tableHeaders],
      tableQuestions: [...tableQuestions],
      tableHeaderImportance: [...tableHeaderImportance],
    };
    const updatedQuestions = [...formInfo.questions];
    updatedQuestions.push(newQuestion);
    setFormInfo({ ...formInfo, questions: updatedQuestions });
    setTableModalOpen(false);
  };

  const questionMeta = {
    text: {
      title: "Free Text Question",
      subtitle: "Patient will answer with an open text response",
      gradient: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[400]} 100%)`,
      icon: <TextFieldsOutlinedIcon />,
      chipLabel: "Text",
    },
    multiple_choice: {
      title: "Multiple Choice Question",
      subtitle: "Configure answer options and importance levels",
      gradient: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[500]} 100%)`,
      icon: <FormatListBulletedOutlinedIcon />,
      chipLabel: "Multiple Choice",
    },
    table: {
      title: "Table Question",
      subtitle: "Score answers across a custom matrix",
      gradient: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
      icon: <TableChartIcon />,
      chipLabel: "Table",
    },
    image: {
      title: "Image Question (ICRS)",
      subtitle: "Select anatomical regions on the visual",
      gradient: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
      icon: <ImageIcon />,
      chipLabel: "Image",
    },
    rating_scale: {
      title: t("forms:ratingScaleQuestion"),
      subtitle: t("forms:ratingScaleSubtitle"),
      gradient: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
      icon: <LinearScaleIcon />,
      chipLabel: "VAS",
    },
    default: {
      title: "Question",
      subtitle: "",
      gradient: `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`,
      icon: <DescriptionOutlinedIcon />,
      chipLabel: "Question",
    },
  };
  // Loading screen
  if (loading) {
    return (
      <Box sx={{ marginLeft: 3, marginRight: 2 }}>
        <Headeer title={"Form Edit"} subtitle={"Loading form data..."} />
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column",
          justifyContent: "center", 
          alignItems: "center", 
          minHeight: "60vh",
          gap: 3
        }}>
          <CircularProgress size={60} sx={{ color: colors.blueAccent[500] }} />
          <Typography variant="h4" sx={{ color: colors.grey[300], fontWeight: 600 }}>
            Loading Form...
          </Typography>
          <Typography variant="body1" sx={{ color: colors.grey[400] }}>
            Please wait while we fetch the form data
          </Typography>
        </Box>
      </Box>
    );
  }

  // No permission screen
  if (!hasPermission) {
    return (
      <Box sx={{ marginLeft: 3, marginRight: 2 }}>
        <Headeer title={"Access Denied"} subtitle={"You don't have permission"} />
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column",
          justifyContent: "center", 
          alignItems: "center", 
          minHeight: "60vh",
          gap: 3,
          textAlign: "center"
        }}>
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${colors.redAccent[600]} 0%, ${colors.redAccent[700]} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 8px 32px ${colors.redAccent[500]}40`,
            }}
          >
            <BlockIcon sx={{ fontSize: 64, color: "white" }} />
          </Box>
          
          <Typography variant="h3" sx={{ color: colors.redAccent[400], fontWeight: 700 }}>
            🚫 Access Denied
          </Typography>
          
          <Typography variant="h5" sx={{ color: colors.grey[300], maxWidth: 600 }}>
            You don't have permission to edit this form
          </Typography>
          
          <Typography variant="body1" sx={{ color: colors.grey[400], maxWidth: 500 }}>
            This form belongs to another doctor or you don't have update permissions for the shared file.
          </Typography>
          
          <Box sx={{ mt: 2 }}>
            <CircularProgress size={30} sx={{ color: colors.blueAccent[500] }} />
            <Typography variant="body2" sx={{ color: colors.grey[400], mt: 1 }}>
              Redirecting you back...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ marginLeft: 3, marginRight: 2 }}>
      {/* Header with Controls Section */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        {/* Header - Left Side */}
        <Box>
          <Headeer title={"Form Edit"} subtitle={"You Can Edit Form Easily"} />
        </Box>

        {/* Controls Section - Total Repetitions and Scoring Formula - Right Side */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
        {/* Repeat Count (Kaç Kere) - Only show for "for patients" */}
        {formInfo?.type === "for patients" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="caption"
        sx={{
                color: colors.grey[300], 
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontWeight: 700
              }}
            >
              🔢 Total Repetitions
              {maxSentCount > 0 && (
                <Typography
                  component="span"
                  sx={{
                    ml: 1,
                    color: colors.grey[400],
                    fontSize: "0.7rem",
                    fontStyle: "italic"
                  }}
                >
                  (Min: {maxSentCount})
                </Typography>
              )}
            </Typography>
            
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => {
                  const currentValue = parseInt(repeatCount) || 0;
                  const newValue = currentValue > 1 ? currentValue - 1 : 1;
                  if (newValue >= maxSentCount) {
                    setRepeatCount(newValue.toString());
                    // Adjust repeat schedule if needed
                    if (newValue === 1) {
                      setRepeatSchedule([]);
                    } else if (newValue < currentValue) {
                      setRepeatSchedule(prev => prev.slice(0, newValue - 1));
                    }
                  }
                }}
                disabled={!repeatCount || parseInt(repeatCount) <= maxSentCount}
                size="small"
                sx={{
                  backgroundColor: colors.redAccent[600],
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: colors.redAccent[700],
                    transform: "scale(1.1)",
                    boxShadow: `0 4px 12px rgba(219, 79, 74, 0.4)`
                  },
                  "&:disabled": {
                    backgroundColor: colors.grey[700],
                    color: colors.grey[500],
                    transform: "scale(1)",
                    boxShadow: "none"
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: `2px solid ${colors.redAccent[500]}`,
                  "&:hover:not(:disabled)": {
                    border: `2px solid ${colors.redAccent[400]}`
                  }
                }}
              >
                <RemoveIcon sx={{ fontSize: 18 }} />
              </IconButton>
              
              <TextField
                type="number"
                value={repeatCount}
                onChange={(e) => {
                  const value = e.target.value;
                  const numValue = parseInt(value) || 0;
                  // Validate: must be >= maxSentCount and <= 999
                  if (value === "" || (numValue >= maxSentCount && numValue <= 999)) {
                    setRepeatCount(value);
                    // Adjust repeat schedule if count changed
                    if (numValue === 1) {
                      setRepeatSchedule([]);
                    } else if (numValue > 1 && repeatSchedule.length !== numValue - 1) {
                      // Adjust schedule array length
                      const newSchedule = [...repeatSchedule];
                      if (newSchedule.length < numValue - 1) {
                        // Add empty items
                        while (newSchedule.length < numValue - 1) {
                          newSchedule.push("");
                        }
                      } else {
                        // Remove excess items
                        newSchedule.splice(numValue - 1);
                      }
                      setRepeatSchedule(newSchedule);
                    }
                  }
                }}
                placeholder="1"
                variant="outlined"
                size="small"
                inputProps={{
                  min: maxSentCount,
                  max: 999,
                  style: { textAlign: "center" }
                }}
                sx={{
                  width: 80,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    textAlign: "center",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    backgroundColor: colors.primary[600],
                    border: `2px solid ${colors.greenAccent[600]}`,
                    "&:hover": {
                      border: `2px solid ${colors.greenAccent[500]}`,
                      backgroundColor: colors.primary[500]
                    },
                    "&.Mui-focused": {
                      border: `2px solid ${colors.greenAccent[400]}`,
                      backgroundColor: colors.primary[400],
                      boxShadow: `0 0 0 3px ${colors.greenAccent[900]}`
                    }
                  },
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                    color: colors.grey[100],
                    fontWeight: "bold"
                  },
                  "& input[type=number]": {
                    "-moz-appearance": "textfield"
                  },
                  "& input[type=number]::-webkit-outer-spin-button": {
                    "-webkit-appearance": "none",
                    margin: 0
                  },
                  "& input[type=number]::-webkit-inner-spin-button": {
                    "-webkit-appearance": "none",
                    margin: 0
                  }
                }}
              />
              
              <IconButton
                onClick={() => {
                  const currentValue = parseInt(repeatCount) || 0;
                  if (currentValue < 999) {
                    setRepeatCount((currentValue + 1).toString());
                    // Add empty schedule item if needed
                    if (currentValue + 1 > 1) {
                      setRepeatSchedule(prev => {
                        const newSchedule = [...prev];
                        if (newSchedule.length < currentValue) {
                          newSchedule.push("");
                        }
                        return newSchedule;
                      });
                    }
                  }
                }}
                disabled={parseInt(repeatCount) >= 999}
                size="small"
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: colors.greenAccent[700],
                    transform: "scale(1.1)",
                    boxShadow: `0 4px 12px rgba(76, 206, 172, 0.4)`
                  },
                  "&:disabled": {
                    backgroundColor: colors.grey[700],
                    color: colors.grey[500],
                    transform: "scale(1)",
                    boxShadow: "none"
                  },
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  border: `2px solid ${colors.greenAccent[500]}`,
                  "&:hover:not(:disabled)": {
                    border: `2px solid ${colors.greenAccent[400]}`
                  }
                }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            
              {/* Configure Schedule Button - Shows when repeatCount > 1 */}
              {parseInt(repeatCount) > 1 && (
                <Button
                  variant="outlined"
                  onClick={() => setScheduleDialogOpen(true)}
                  sx={{
                    ml: 1,
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 2,
                    py: 0.5,
                    borderColor: colors.blueAccent[500],
                    color: colors.blueAccent[400],
                    backgroundColor: colors.primary[600],
                    "&:hover": {
                      borderColor: colors.blueAccent[400],
                      backgroundColor: colors.blueAccent[700],
                      color: colors.grey[100],
                    },
                  }}
                >
                  ⚙️ Configure Schedule
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* Scoring Formula Selection */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ 
              color: colors.grey[300], 
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 0.5
            }}
          >
            Scoring Formula (Optional)
          </Typography>
          <FormControl
            sx={{
              minWidth: 250,
              maxWidth: { xs: "100%", sm: 420 },
              width: "100%",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                backgroundColor: colors.primary[600],
                border: selectedFormulaId 
                  ? `2px solid ${colors.greenAccent[600]}` 
                  : `2px solid ${colors.grey[700]}`,
                "&:hover": {
                  border: selectedFormulaId
                    ? `2px solid ${colors.greenAccent[500]}`
                    : `2px solid ${colors.grey[600]}`,
                  backgroundColor: colors.primary[500],
                },
                "&.Mui-focused": {
                  border: `2px solid ${colors.blueAccent[400]}`,
                  backgroundColor: colors.primary[400],
                  boxShadow: `0 0 0 3px ${colors.blueAccent[900]}`,
                },
              },
            }}
          >
            <Select
              value={selectedFormulaId}
              onChange={(e) => setSelectedFormulaId(e.target.value)}
              displayEmpty
              IconComponent={ExpandMoreIcon}
              renderValue={(value) => {
                if (value === "" || value == null) {
                  return <em>No formula (Optional)</em>;
                }
                const f = formulas.find(
                  (x) => String(x.id) === String(value)
                );
                return f ? (
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                      maxWidth: "100%",
                    }}
                  >
                    {f.name}
                  </Typography>
                ) : (
                  ""
                );
              }}
              sx={{
                borderRadius: 2,
                "& .MuiSelect-select": {
                  padding: "10px 14px",
                  color: selectedFormulaId ? colors.grey[100] : colors.grey[400],
                  fontWeight: selectedFormulaId ? 600 : 500,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 2,
                    mt: 1,
                    maxHeight: 300,
                    maxWidth: { xs: "calc(100vw - 24px)", sm: 420 },
                    backgroundColor: colors.primary[400],
                    "& .MuiMenuItem-root": {
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      maxWidth: "100%",
                      "&:hover": {
                        backgroundColor: colors.blueAccent[700],
                        color: "white",
                      },
                      "&.Mui-selected": {
                        backgroundColor: colors.blueAccent[800],
                        "&:hover": {
                          backgroundColor: colors.blueAccent[700],
                        },
                      },
                    },
                  },
                },
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
              }}
            >
              <MenuItem value="">
                <em>No formula (Optional)</em>
              </MenuItem>
              {formulas.map((formula) => (
                <MenuItem key={formula.id} value={formula.id}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                    title={formula.name}
                  >
                    {formula.name}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        </Box>
      </Box>
      <Box sx={{ width: "90%", ml: 6 }}>
        <Box
          sx={{
            mb: 4,
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: `0 14px 32px ${colors.primary[900]}40`,
            border: `1px solid ${colors.primary[200]}30`,
            backgroundColor: colors.primary[500],
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              px: { xs: 2.5, md: 3.5 },
              py: 2.5,
              background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: 3,
                  backgroundColor: colors.primary[500],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: colors.grey[100],
                  boxShadow: `0 10px 26px ${colors.primary[900]}55`,
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 30 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: colors.grey[100],
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    fontSize: "1.1rem",
                  }}
                >
                  Form Details
                </Typography>
                <Typography sx={{ color: colors.grey[200], opacity: 0.85 }}>
                  Update the form title and description to match what patients will see.
                </Typography>
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                flexWrap: "wrap",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
              }}
            >
              <Chip
                label={`File: ${formInfo?.File?.name || "—"}`}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 1,
                }}
              />
              <Chip
                label={`Type: ${formInfo?.type === "for me" ? "For Doctor" : "For Patient"}`}
                sx={{
                  backgroundColor: `${colors.primary[500]}aa`,
                  color: colors.grey[100],
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 1,
                }}
              />
            </Stack>
          </Box>

          <Box
            sx={{
              px: { xs: 2.5, md: 3.5 },
              py: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: colors.grey[300],
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Form Title
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="e.g. Knee Assessment Questionnaire"
                value={formInfo?.name || ""}
                onChange={(e) => {
                  setFormInfo({ ...formInfo, name: e.target.value });
                }}
                InputProps={{
                  sx: {
                    borderRadius: 3,
                    backgroundColor: colors.primary[500],
                    fontSize: "1.7rem",
                    fontWeight: 700,
                    color: colors.grey[100],
                    "& fieldset": {
                      borderColor: `${colors.primary[200]}44`,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.greenAccent[400],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.greenAccent[500],
                      boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    color: colors.grey[400],
                    "&.Mui-focused": {
                      color: colors.greenAccent[300],
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "right", mt: 0.5, color: colors.grey[400] }}
              >
                {(formInfo?.name?.length ?? 0)} characters
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 1,
                  color: colors.grey[300],
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Form Description
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                multiline
                minRows={3}
                placeholder="Provide a short summary to help patients understand the form."
                value={formInfo?.description || ""}
                onChange={(e) => {
                  setFormInfo({ ...formInfo, description: e.target.value });
                }}
                InputProps={{
                  sx: {
                    borderRadius: 3,
                    backgroundColor: colors.primary[500],
                    color: colors.grey[200],
                    "& fieldset": {
                      borderColor: `${colors.primary[200]}33`,
                    },
                    "&:hover fieldset": {
                      borderColor: colors.blueAccent[300],
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.blueAccent[200],
                      boxShadow: `0 0 0 3px ${colors.blueAccent[400]}33`,
                    },
                  },
                }}
                InputLabelProps={{
                  sx: {
                    color: colors.grey[400],
                    "&.Mui-focused": {
                      color: colors.blueAccent[300],
                    },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ display: "block", textAlign: "right", mt: 0.5, color: colors.grey[400] }}
              >
                {(formInfo?.description?.length ?? 0)} characters
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box>
          {formInfo?.questions?.map((field, index) => {
            const meta = questionMeta[field.type] || questionMeta.default;

            return (
              <Box key={index} sx={{ mb: 4 }}>
                <Box
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: `0 12px 30px ${colors.primary[900]}40`,
                    border: `1px solid ${colors.primary[200]}30`,
                    backgroundColor: colors.primary[500],
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: { xs: 2.5, md: 3.5 },
                      py: 2.5,
                      background: meta.gradient,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 3,
                          backgroundColor: colors.primary[500],
                          color: colors.grey[100],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: `0 8px 20px ${colors.primary[900]}55`,
                        }}
                      >
                        {meta.icon}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: colors.grey[100],
                            letterSpacing: 0.4,
                          }}
                        >
                          Question {index + 1}: {meta.title}
                        </Typography>
                        {meta.subtitle && (
                          <Typography
                            variant="body2"
                            sx={{ color: colors.grey[200], opacity: 0.85 }}
                          >
                            {meta.subtitle}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Chip
                      label={meta.chipLabel}
                      sx={{
                        backgroundColor: `${colors.primary[500]}aa`,
                        color: colors.grey[100],
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: 0.6,
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      px: { xs: 2.5, md: 3.5 },
                      py: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                    }}
                  >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      color: colors.grey[300],
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Question Text
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Describe the patient's current symptoms"
                    value={field.question || ""}
                    onChange={(e) => {
                      const updatedQuestions = [...formInfo.questions];
                      updatedQuestions[index].question = e.target.value;
                      setFormInfo({ ...formInfo, questions: updatedQuestions });
                    }}
                    InputProps={{
                      sx: {
                        borderRadius: 3,
                        backgroundColor: colors.primary[500],
                        fontSize: "1.05rem",
                        fontWeight: 600,
                        color: colors.grey[100],
                        "& fieldset": {
                          borderColor: `${colors.primary[200]}44`,
                        },
                        "&:hover fieldset": {
                          borderColor: colors.greenAccent[400],
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.greenAccent[500],
                          boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                        },
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textAlign: "right",
                      mt: 0.5,
                      color: colors.grey[400],
                    }}
                  >
                    {(field.question?.length ?? 0)} characters
                  </Typography>
                </Box>

                {field.type === "text" && (
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backgroundColor: `${colors.primary[600]}99`,
                      border: `1px solid ${colors.primary[200]}22`,
                      maxWidth: 760,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: colors.grey[300],
                        mb: 1,
                        letterSpacing: 0.4,
                      }}
                    >
                      Patient Preview
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Patient response will appear here"
                      disabled
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          backgroundColor: colors.primary[500],
                          color: colors.grey[400],
                          '& fieldset': {
                            borderColor: `${colors.primary[200]}33`,
                          },
                        },
                      }}
                    />
                  </Box>
                )}

                {field.type === "multiple_choice" && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: colors.grey[300],
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                      }}
                    >
                      Options
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {field.options.map((optionObj, optionIdx) => (
                        <Box
                          key={optionIdx}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "auto 1fr", md: "auto 1fr 180px auto" },
                            gap: { xs: 1.5, md: 2 },
                            alignItems: "center",
                            backgroundColor: `${colors.primary[600]}AA`,
                            borderRadius: 3,
                            border: `1px solid ${colors.primary[200]}33`,
                            boxShadow: `0 8px 18px ${colors.primary[900]}25`,
                            px: { xs: 1.5, md: 2.5 },
                            py: { xs: 1.2, md: 1.6 },
                          }}
                        >
                          <Radio
                            disabled
                            sx={{
                              color: colors.greenAccent[500],
                              '&.Mui-disabled': {
                                color: colors.greenAccent[600],
                              },
                            }}
                          />

                          <MuiTextField
                            value={optionObj.option}
                            label={`Option ${optionIdx + 1}`}
                            variant="outlined"
                            fullWidth
                            onChange={(e) => {
                              const updatedQuestions = [...formInfo.questions];
                              updatedQuestions[index].options[optionIdx].option = e.target.value;
                              setFormInfo({ ...formInfo, questions: updatedQuestions });
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: colors.primary[500],
                                '& fieldset': {
                                  borderColor: `${colors.primary[200]}33`,
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.greenAccent[400],
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.greenAccent[500],
                                  boxShadow: `0 0 0 3px ${colors.greenAccent[500]}33`,
                                },
                              },
                              '& .MuiInputLabel-root': {
                                color: colors.grey[400],
                                '&.Mui-focused': {
                                  color: colors.greenAccent[300],
                                },
                              },
                            }}
                          />

                          <MuiTextField
                            type="number"
                            value={optionObj.option_level ?? ""}
                            label="Importance"
                            variant="outlined"
                            onChange={(e) => {
                              const updatedQuestions = [...formInfo.questions];
                              updatedQuestions[index].options[optionIdx].option_level =
                                e.target.value === "" ? 0 : Number(e.target.value);
                              setFormInfo({ ...formInfo, questions: updatedQuestions });
                            }}
                            sx={{
                              width: { xs: "100%", md: "160px" },
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: colors.primary[500],
                                '& fieldset': {
                                  borderColor: `${colors.primary[200]}33`,
                                },
                                '&:hover fieldset': {
                                  borderColor: colors.blueAccent[400],
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: colors.blueAccent[300],
                                  boxShadow: `0 0 0 3px ${colors.blueAccent[300]}33`,
                                },
                              },
                              '& .MuiInputBase-input': {
                                textAlign: "center",
                                color: colors.grey[100],
                                fontWeight: 600,
                              },
                              '& .MuiInputLabel-root': {
                                color: colors.grey[400],
                                '&.Mui-focused': {
                                  color: colors.blueAccent[300],
                                },
                              },
                            }}
                          />

                          <Button
                            variant="outlined"
                            size="small"
                            sx={{
                              justifySelf: { xs: "stretch", md: "end" },
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 2,
                              backgroundColor: colors.redAccent[600],
                              color: "white",
                              px: 2,
                              '&:hover': {
                                backgroundColor: colors.redAccent[500],
                              },
                            }}
                            onClick={() => {
                              const updatedQuestions = [...formInfo.questions];
                              if (updatedQuestions[index].options.length > 2) {
                                updatedQuestions[index].options.splice(optionIdx, 1);
                              }
                              setFormInfo({ ...formInfo, questions: updatedQuestions });
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const updatedQuestions = [...formInfo.questions];
                          updatedQuestions[index].options.push({
                            option: "",
                            option_level: 0,
                          });
                          setFormInfo({ ...formInfo, questions: updatedQuestions });
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 2.5,
                          backgroundColor: colors.primary[500],
                          color: colors.grey[100],
                          '&:hover': {
                            backgroundColor: colors.primary[400],
                            borderColor: colors.greenAccent[400],
                          },
                        }}
                      >
                        ➕ Add Option
                      </Button>
                    </Box>
                  </Box>
                )}

                {field.type === "image" && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 2,
                      mb: 3
                    }}>
                      <ImageIcon sx={{ 
                        fontSize: 28,
                        color: colors.greenAccent[500]
                      }} />
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: "bold",
                          background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent"
                        }}
                      >
                        Visual Question (ICRS) - Edit Mode
                      </Typography>
                      <Box sx={{ 
                        ml: "auto",
                        px: 2, 
                        py: 0.5, 
                        borderRadius: 2,
                        backgroundColor: colors.blueAccent[700],
                        border: `1px solid ${colors.blueAccent[500]}`
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                          🎯 Areas: {field.areas?.length || 0}
                        </Typography>
                      </Box>
                    </Box>

                    <ImageQuestion
                      questionData={{
                        question: field.question,
                        imageUrl: field.imageUrl || '',
                        imageAnswerMode: field.imageAnswerMode || "select",
                        areas: field.areas || []
                      }}
                      onQuestionDataChange={(newData) => {
                        const updatedQuestions = [...formInfo.questions];
                        updatedQuestions[index] = {
                          ...updatedQuestions[index],
                          ...newData
                        };
                        setFormInfo({ ...formInfo, questions: updatedQuestions });
                      }}
                    />
                  </Box>
                )}

                {field.type === "rating_scale" && (
                  <Box sx={{ mt: 1 }}>
                    <RatingScaleQuestionEditor
                      field={field}
                      colors={colors}
                      excludeAllFromScoring={false}
                      t={t}
                      onFieldChange={(updates) => {
                        const updatedQuestions = [...formInfo.questions];
                        updatedQuestions[index] = {
                          ...updatedQuestions[index],
                          ...updates,
                        };
                        setFormInfo({ ...formInfo, questions: updatedQuestions });
                      }}
                    />
                  </Box>
                )}

                {field.type === "table" && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ 
                      display: "flex", 
                      alignItems: "center", 
                      gap: 2,
                      mb: 3
                    }}>
                      <TableChartIcon sx={{ 
                        fontSize: 28,
                        color: colors.greenAccent[500]
                      }} />
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          fontWeight: "bold",
                          background: `linear-gradient(135deg, ${colors.greenAccent[500]} 0%, ${colors.blueAccent[500]} 100%)`,
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent"
                        }}
                      >
                        Table Question
                      </Typography>
                      <Box sx={{ 
                        ml: "auto",
                        display: "flex",
                        gap: 1,
                        alignItems: "center"
                      }}>
                        <Box sx={{ 
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 2,
                          backgroundColor: colors.blueAccent[700],
                          border: `1px solid ${colors.blueAccent[500]}`
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                            📝 Rows: {field.tableRows}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          px: 2, 
                          py: 0.5, 
                          borderRadius: 2,
                          backgroundColor: colors.greenAccent[700],
                          border: `1px solid ${colors.greenAccent[500]}`
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                            📊 Columns: {field.tableCols}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ 
                      width: "100%",
                      maxWidth: "100%",
                      overflowX: "hidden",
                      borderRadius: 2,
                      border: `3px solid ${colors.greenAccent[700]}`,
                      boxShadow: `0 4px 16px rgba(0, 0, 0, 0.2)`,
                      background: colors.primary[500]
                    }}>
                      <Table
                        size="small"
                        sx={{ tableLayout: "fixed", width: "100%" }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell sx={tableCellShrinkBase(1 + (field.tableHeaders?.length || field.tableCols || 1), {
                              background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
                              fontWeight: "bold",
                              border: `2px solid ${colors.blueAccent[600]}`,
                              color: colors.grey[100],
                              fontSize: "0.85rem",
                              textAlign: "center",
                              px: 0.75,
                              py: 1,
                            })}>
                              ❓ Questions
                            </TableCell>
                            {field.tableHeaders?.map((header, headerIdx) => (
                              <TableCell 
                                key={headerIdx}
                                sx={tableCellShrinkBase(1 + (field.tableHeaders?.length || field.tableCols || 1), {
                                  background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
                                  fontWeight: "bold",
                                  border: `2px solid ${colors.greenAccent[600]}`,
                                  px: 0.75,
                                  py: 1,
                                })}
                              >
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                                  <MuiTextField
                                    value={header}
                                    onChange={(e) => {
                                      const updatedQuestions = [...formInfo.questions];
                                      updatedQuestions[index].tableHeaders[headerIdx] = e.target.value;
                                      setFormInfo({ ...formInfo, questions: updatedQuestions });
                                    }}
                                    placeholder="Option name"
                                    variant="standard"
                                    size="small"
                                    fullWidth
                                    sx={{ 
                                      minWidth: 0,
                                      "& .MuiInput-root": { 
                                        fontSize: "0.9rem",
                                        fontWeight: "bold",
                                        color: colors.grey[100]
                                      },
                                      "& .MuiInput-root:before": {
                                        borderBottomColor: colors.greenAccent[400]
                                      },
                                      "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                        borderBottomColor: colors.greenAccent[300]
                                      },
                                      "& .MuiInput-root:after": {
                                        borderBottomColor: colors.greenAccent[200]
                                      }
                                    }}
                                    InputProps={{
                                      style: { color: colors.grey[100] }
                                    }}
                                  />
                                  
                                  <Box sx={{ 
                                    display: "flex", 
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexWrap: "wrap",
                                    gap: 0.75,
                                    backgroundColor: colors.primary[600],
                                    borderRadius: 2,
                                    p: 0.75
                                  }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const updatedQuestions = [...formInfo.questions];
                                        const currentValue = updatedQuestions[index].tableHeaderImportance?.[headerIdx] || 0;
                                        if (!updatedQuestions[index].tableHeaderImportance) {
                                          updatedQuestions[index].tableHeaderImportance = new Array(field.tableHeaders.length).fill(0);
                                        }
                                        updatedQuestions[index].tableHeaderImportance[headerIdx] = Math.max(0, currentValue - 1);
                                        setFormInfo({ ...formInfo, questions: updatedQuestions });
                                      }}
                                      sx={{
                                        backgroundColor: colors.redAccent[700],
                                        color: "white",
                                        width: 24,
                                        height: 24,
                                        "&:hover": {
                                          backgroundColor: colors.redAccent[600],
                                          transform: "scale(1.1)"
                                        },
                                        transition: "all 0.2s"
                                      }}
                                    >
                                      <RemoveIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    
                                    <MuiTextField
                                      type="number"
                                      value={field.tableHeaderImportance?.[headerIdx] ?? 0}
                                      onChange={(e) => {
                                        const updatedQuestions = [...formInfo.questions];
                                        if (!updatedQuestions[index].tableHeaderImportance) {
                                          updatedQuestions[index].tableHeaderImportance = new Array(field.tableHeaders.length).fill(0);
                                        }
                                        updatedQuestions[index].tableHeaderImportance[headerIdx] = e.target.value === "" ? 0 : Number(e.target.value);
                                        setFormInfo({ ...formInfo, questions: updatedQuestions });
                                      }}
                                      placeholder="0"
                                      variant="standard"
                                      size="small"
                                      sx={{ 
                                        width: 60,
                                        "& .MuiInput-root": { 
                                          fontSize: "0.9rem",
                                          fontWeight: "bold",
                                          color: colors.grey[100],
                                          textAlign: "center"
                                        },
                                        "& .MuiInput-root:before": {
                                          borderBottomColor: colors.greenAccent[400]
                                        },
                                        "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                          borderBottomColor: colors.greenAccent[300]
                                        },
                                        "& .MuiInput-root:after": {
                                          borderBottomColor: colors.greenAccent[200]
                                        }
                                      }}
                                      inputProps={{
                                        style: { textAlign: "center", color: colors.grey[100] },
                                        min: 0
                                      }}
                                    />
                                    
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        const updatedQuestions = [...formInfo.questions];
                                        const currentValue = updatedQuestions[index].tableHeaderImportance?.[headerIdx] || 0;
                                        if (!updatedQuestions[index].tableHeaderImportance) {
                                          updatedQuestions[index].tableHeaderImportance = new Array(field.tableHeaders.length).fill(0);
                                        }
                                        updatedQuestions[index].tableHeaderImportance[headerIdx] = currentValue + 1;
                                        setFormInfo({ ...formInfo, questions: updatedQuestions });
                                      }}
                                      sx={{
                                        backgroundColor: colors.greenAccent[700],
                                        color: "white",
                                        width: 24,
                                        height: 24,
                                        "&:hover": {
                                          backgroundColor: colors.greenAccent[600],
                                          transform: "scale(1.1)"
                                        },
                                        transition: "all 0.2s"
                                      }}
                                    >
                                      <AddIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Box>
                                  
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: colors.grey[400],
                                      fontSize: "0.7rem",
                                      fontStyle: "italic",
                                      textAlign: "center"
                                    }}
                                  >
                                    💯 Score: {field.tableHeaderImportance?.[headerIdx] ?? 0}
                                  </Typography>
                                </Box>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {field.tableQuestions?.map((question, questionIdx) => (
                            <TableRow 
                              key={questionIdx}
                              sx={{
                                "&:hover": {
                                  backgroundColor: colors.primary[600]
                                },
                                transition: "background-color 0.2s"
                              }}
                            >
                              <TableCell sx={tableCellShrinkBase(1 + (field.tableHeaders?.length || field.tableCols || 1), {
                                backgroundColor: colors.blueAccent[900],
                                border: `1px solid ${colors.blueAccent[700]}`,
                                px: 0.75,
                                py: 0.75,
                              })}>
                                <MuiTextField
                                  value={question}
                                  onChange={(e) => {
                                    const updatedQuestions = [...formInfo.questions];
                                    updatedQuestions[index].tableQuestions[questionIdx] = e.target.value;
                                    setFormInfo({ ...formInfo, questions: updatedQuestions });
                                  }}
                                  variant="standard"
                                  size="small"
                                  fullWidth
                                  sx={{ 
                                    minWidth: 0,
                                    "& .MuiInput-root": { 
                                      fontSize: "0.85rem",
                                      color: colors.grey[100]
                                    },
                                    "& .MuiInput-root:before": {
                                      borderBottomColor: colors.blueAccent[400]
                                    },
                                    "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                      borderBottomColor: colors.blueAccent[300]
                                    },
                                    "& .MuiInput-root:after": {
                                      borderBottomColor: colors.blueAccent[200]
                                    }
                                  }}
                                  InputProps={{
                                    style: { color: colors.grey[100] }
                                  }}
                                />
                              </TableCell>
                              {field.tableHeaders?.map((header, headerIdx) => (
                                <TableCell 
                                  key={headerIdx}
                                  sx={tableCellShrinkBase(1 + (field.tableHeaders?.length || field.tableCols || 1), {
                                    border: `1px solid ${colors.greenAccent[700]}`,
                                    textAlign: "center",
                                    backgroundColor: colors.primary[600],
                                    px: 0.5,
                                    py: 0.5,
                                    "&:hover": {
                                      backgroundColor: colors.primary[500]
                                    },
                                    transition: "background-color 0.2s"
                                  })}
                                >
                                  <Radio 
                                    disabled 
                                    size="small"
                                    sx={{
                                      color: colors.greenAccent[600],
                                      "&.Mui-disabled": {
                                        color: colors.greenAccent[700]
                                      }
                                    }}
                                  />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                    <Box sx={{ 
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: colors.primary[600],
                      border: `1px solid ${colors.blueAccent[700]}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 1
                    }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: colors.blueAccent[400],
                          fontStyle: "italic"
                        }}
                      >
                        ✏️ Edit headers and questions by clicking on them
                      </Typography>
                    </Box>
                  </Box>
                )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        onClick={() => {
                          const updatedQuestions = [...formInfo.questions];
                          updatedQuestions.splice(index, 1);
                          setFormInfo({ ...formInfo, questions: updatedQuestions });
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          px: 3,
                          py: 1.1,
                          borderRadius: 2.5,
                          background: `linear-gradient(135deg, ${colors.redAccent[500]} 0%, ${colors.redAccent[600]} 100%)`,
                          color: "white",
                          boxShadow: `0 8px 22px ${colors.redAccent[500]}45`,
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: `0 10px 26px ${colors.redAccent[500]}60`,
                          },
                        }}
                      >
                        Delete Question
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
          <Box sx={{ marginTop: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: colors.grey[300],
                mb: 1.5,
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              {t("forms:addQuestions")}
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
              <FormGradientButton
                preset="addText"
                onClick={() => {
                  const updatedQuestions = [...formInfo.questions];
                  const newQuestion = {
                    question: "",
                    type: "text",
                  };
                  updatedQuestions.push(newQuestion);
                  setFormInfo({ ...formInfo, questions: updatedQuestions });
                }}
              >
                {t("forms:addTextQuestion")}
              </FormGradientButton>
              <FormGradientButton
                preset="addMultipleChoice"
                onClick={() => {
                  const updatedQuestions = [...formInfo.questions];
                  const newQuestion = {
                    options: [
                      { option: "Option 1", option_level: 0 },
                      { option: "Option 2", option_level: 0 },
                    ],
                    question: "",
                    type: "multiple_choice",
                  };
                  updatedQuestions.push(newQuestion);
                  setFormInfo({ ...formInfo, questions: updatedQuestions });
                }}
              >
                {t("forms:addMultipleChoice")}
              </FormGradientButton>
              <FormGradientButton
                preset="addTable"
                startIcon={<TableChartIcon />}
                onClick={handleAddTableQuestion}
              >
                {t("forms:addTableQuestion")}
              </FormGradientButton>
              <FormGradientButton
                preset="addImage"
                startIcon={<ImageIcon />}
                onClick={() => {
                  const updatedQuestions = [...formInfo.questions];
                  const newQuestion = {
                    question: "",
                    type: "image",
                    imageUrl: "",
                    imageAnswerMode: "select",
                    areas: [],
                  };
                  updatedQuestions.push(newQuestion);
                  setFormInfo({ ...formInfo, questions: updatedQuestions });
                }}
              >
                {t("forms:addImageQuestionICRS")}
              </FormGradientButton>
              <FormGradientButton
                preset="addRatingScale"
                startIcon={<LinearScaleIcon />}
                onClick={() => {
                  const updatedQuestions = [...(formInfo.questions || [])];
                  updatedQuestions.push({
                    type: "rating_scale",
                    question: "",
                    category: "",
                    hasImportance: true,
                    minLabel: "",
                    maxLabel: "",
                    positiveEnd: "min",
                    scaleImportances: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
                  });
                  setFormInfo({ ...formInfo, questions: updatedQuestions });
                }}
              >
                {t("forms:addRatingScaleQuestion")}
              </FormGradientButton>
            </Stack>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 3,
            flexWrap: "wrap",
          }}
        >
          <FormGradientButton
            preset="cancel"
            onClick={() => handleDialogOpen("cancel")}
            disabled={saving}
          >
            {t("forms:cancel")}
          </FormGradientButton>
          <FormGradientButton
            preset="save"
            onClick={() => handleDialogOpen("save")}
            disabled={saving}
          >
            {saving ? t("forms:savingForm") : t("common:save")}
          </FormGradientButton>
        </Box>
        {/* Table Question Creation Modal */}
        <Dialog 
          open={tableModalOpen} 
          onClose={() => setTableModalOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              background: colors.primary[400],
              borderRadius: 3,
              boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
            }
          }}
        >
          <DialogTitle sx={{ 
            borderBottom: `2px solid ${colors.greenAccent[400]}`,
            pb: 2
          }}>
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 2,
              background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent"
            }}>
              <TableChartIcon sx={{ 
                fontSize: 32,
                color: colors.greenAccent[500]
              }} />
              <Typography variant="h4" fontWeight="bold">
                Create Table Question
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mt: 1 }}>
              {/* Table Size Controls */}
              <Box sx={{ 
                mb: 4, 
                p: 3, 
                borderRadius: 3,
                background: `linear-gradient(135deg, ${colors.primary[300]} 0%, ${colors.primary[400]} 100%)`,
                border: `2px solid ${colors.greenAccent[700]}`,
                boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15)`
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 3, 
                    color: colors.greenAccent[400],
                    fontWeight: "bold"
                  }}
                >
                  📐 Table Size Configuration
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      display: "flex", 
                      flexDirection: "column",
                      alignItems: "center",
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: colors.primary[500],
                      border: `1px solid ${colors.blueAccent[700]}`
                    }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: "bold",
                          color: colors.blueAccent[400]
                        }}
                      >
                        📝 Rows (Questions)
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <IconButton 
                          onClick={() => handleTableRowsChange(tableRows - 1)}
                          disabled={tableRows <= 2}
                          size="medium"
                          sx={{ 
                            backgroundColor: colors.redAccent[600],
                            color: "white",
                            "&:hover": { 
                              backgroundColor: colors.redAccent[700],
                              transform: "scale(1.1)"
                            },
                            "&:disabled": {
                              backgroundColor: colors.grey[700],
                              color: colors.grey[500]
                            },
                            transition: "all 0.2s"
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Box sx={{ 
                          minWidth: 60, 
                          textAlign: "center",
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: colors.blueAccent[800],
                          border: `2px solid ${colors.blueAccent[600]}`
                        }}>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: "bold",
                              color: colors.blueAccent[300]
                            }}
                          >
                            {tableRows}
                          </Typography>
                        </Box>
                        <IconButton 
                          onClick={() => handleTableRowsChange(tableRows + 1)}
                          disabled={tableRows >= 20}
                          size="medium"
                          sx={{ 
                            backgroundColor: colors.greenAccent[600],
                            color: "white",
                            "&:hover": { 
                              backgroundColor: colors.greenAccent[700],
                              transform: "scale(1.1)"
                            },
                            "&:disabled": {
                              backgroundColor: colors.grey[700],
                              color: colors.grey[500]
                            },
                            transition: "all 0.2s"
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          mt: 1, 
                          color: colors.grey[400],
                          fontSize: "0.75rem"
                        }}
                      >
                        Min: 2, Max: 20
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      display: "flex", 
                      flexDirection: "column",
                      alignItems: "center",
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: colors.primary[500],
                      border: `1px solid ${colors.greenAccent[700]}`
                    }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          mb: 2, 
                          fontWeight: "bold",
                          color: colors.greenAccent[400]
                        }}
                      >
                        📊 Columns (Options)
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <IconButton 
                          onClick={() => handleTableColsChange(tableCols - 1)}
                          disabled={tableCols <= 2}
                          size="medium"
                          sx={{ 
                            backgroundColor: colors.redAccent[600],
                            color: "white",
                            "&:hover": { 
                              backgroundColor: colors.redAccent[700],
                              transform: "scale(1.1)"
                            },
                            "&:disabled": {
                              backgroundColor: colors.grey[700],
                              color: colors.grey[500]
                            },
                            transition: "all 0.2s"
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Box sx={{ 
                          minWidth: 60, 
                          textAlign: "center",
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: colors.greenAccent[800],
                          border: `2px solid ${colors.greenAccent[600]}`
                        }}>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: "bold",
                              color: colors.greenAccent[300]
                            }}
                          >
                            {tableCols}
                          </Typography>
                        </Box>
                        <IconButton 
                          onClick={() => handleTableColsChange(tableCols + 1)}
                          disabled={tableCols >= 15}
                          size="medium"
                          sx={{ 
                            backgroundColor: colors.greenAccent[600],
                            color: "white",
                            "&:hover": { 
                              backgroundColor: colors.greenAccent[700],
                              transform: "scale(1.1)"
                            },
                            "&:disabled": {
                              backgroundColor: colors.grey[700],
                              color: colors.grey[500]
                            },
                            transition: "all 0.2s"
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          mt: 1, 
                          color: colors.grey[400],
                          fontSize: "0.75rem"
                        }}
                      >
                        Min: 2, Max: 15
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Table Preview */}
              <Box sx={{ 
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: colors.blueAccent[400],
                    fontWeight: "bold"
                  }}
                >
                  👁️ Table Preview
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: colors.grey[400],
                    ml: 1
                  }}
                >
                  (Edit headers and questions directly)
                </Typography>
              </Box>
              <Box sx={{ 
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
                borderRadius: 2,
                border: `3px solid ${colors.greenAccent[700]}`,
                boxShadow: `0 4px 16px rgba(0, 0, 0, 0.2)`,
                background: colors.primary[500]
              }}>
                <Table
                  size="small"
                  sx={{ tableLayout: "fixed", width: "100%" }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableCellShrinkBase(1 + (tableHeaders?.length || tableCols || 1), {
                        background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
                        fontWeight: "bold",
                        border: `2px solid ${colors.blueAccent[600]}`,
                        color: colors.grey[100],
                        fontSize: "0.85rem",
                        textAlign: "center",
                        px: 0.75,
                        py: 1,
                      })}>
                        ❓ Questions
                      </TableCell>
                      {tableHeaders.map((header, headerIdx) => (
                        <TableCell 
                          key={headerIdx}
                          sx={tableCellShrinkBase(1 + (tableHeaders?.length || tableCols || 1), {
                            background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`,
                            fontWeight: "bold",
                            border: `2px solid ${colors.greenAccent[600]}`,
                            px: 0.75,
                            py: 1,
                          })}
                        >
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                            <MuiTextField
                              value={header}
                              onChange={(e) => handleTableHeaderChange(headerIdx, e.target.value)}
                              placeholder="Option name"
                              variant="standard"
                              size="small"
                              fullWidth
                              sx={{ 
                                minWidth: 0,
                                "& .MuiInput-root": { 
                                  fontSize: "0.9rem",
                                  fontWeight: "bold",
                                  color: colors.grey[100]
                                },
                                "& .MuiInput-root:before": {
                                  borderBottomColor: colors.greenAccent[400]
                                },
                                "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                  borderBottomColor: colors.greenAccent[300]
                                },
                                "& .MuiInput-root:after": {
                                  borderBottomColor: colors.greenAccent[200]
                                }
                              }}
                              InputProps={{
                                style: { color: colors.grey[100] }
                              }}
                            />
                            
                            <Box sx={{ 
                              display: "flex", 
                              alignItems: "center",
                              justifyContent: "center",
                              flexWrap: "wrap",
                              gap: 0.75,
                              backgroundColor: colors.primary[600],
                              borderRadius: 2,
                              p: 0.75
                            }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const currentValue = tableHeaderImportance[headerIdx];
                                  handleTableHeaderImportanceChange(headerIdx, Math.max(0, currentValue - 1));
                                }}
                                sx={{
                                  backgroundColor: colors.redAccent[700],
                                  color: "white",
                                  width: 24,
                                  height: 24,
                                  "&:hover": {
                                    backgroundColor: colors.redAccent[600],
                                    transform: "scale(1.1)"
                                  },
                                  transition: "all 0.2s"
                                }}
                              >
                                <RemoveIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              
                              <MuiTextField
                                type="number"
                                value={tableHeaderImportance[headerIdx]}
                                onChange={(e) => handleTableHeaderImportanceChange(headerIdx, e.target.value)}
                                placeholder="0"
                                variant="standard"
                                size="small"
                                sx={{ 
                                  width: 60,
                                  "& .MuiInput-root": { 
                                    fontSize: "0.9rem",
                                    fontWeight: "bold",
                                    color: colors.grey[100],
                                    textAlign: "center"
                                  },
                                  "& .MuiInput-root:before": {
                                    borderBottomColor: colors.greenAccent[400]
                                  },
                                  "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                    borderBottomColor: colors.greenAccent[300]
                                  },
                                  "& .MuiInput-root:after": {
                                    borderBottomColor: colors.greenAccent[200]
                                  }
                                }}
                                inputProps={{
                                  style: { textAlign: "center", color: colors.grey[100] },
                                  min: 0
                                }}
                              />
                              
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const currentValue = tableHeaderImportance[headerIdx];
                                  handleTableHeaderImportanceChange(headerIdx, currentValue + 1);
                                }}
                                sx={{
                                  backgroundColor: colors.greenAccent[700],
                                  color: "white",
                                  width: 24,
                                  height: 24,
                                  "&:hover": {
                                    backgroundColor: colors.greenAccent[600],
                                    transform: "scale(1.1)"
                                  },
                                  transition: "all 0.2s"
                                }}
                              >
                                <AddIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                            
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: colors.grey[400],
                                fontSize: "0.7rem",
                                fontStyle: "italic",
                                textAlign: "center"
                              }}
                            >
                              💯 Score: {tableHeaderImportance[headerIdx]}
                            </Typography>
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableQuestions.map((question, questionIdx) => (
                      <TableRow 
                        key={questionIdx}
                        sx={{
                          "&:hover": {
                            backgroundColor: colors.primary[600]
                          },
                          transition: "background-color 0.2s"
                        }}
                      >
                        <TableCell sx={tableCellShrinkBase(1 + (tableHeaders?.length || tableCols || 1), {
                          backgroundColor: colors.blueAccent[900],
                          border: `1px solid ${colors.blueAccent[700]}`,
                          px: 0.75,
                          py: 0.75,
                        })}>
                          <MuiTextField
                            value={question}
                            onChange={(e) => handleTableQuestionChange(questionIdx, e.target.value)}
                            variant="standard"
                            size="small"
                            fullWidth
                            sx={{ 
                              minWidth: 0,
                              "& .MuiInput-root": { 
                                fontSize: "0.85rem",
                                color: colors.grey[100]
                              },
                              "& .MuiInput-root:before": {
                                borderBottomColor: colors.blueAccent[400]
                              },
                              "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                                borderBottomColor: colors.blueAccent[300]
                              },
                              "& .MuiInput-root:after": {
                                borderBottomColor: colors.blueAccent[200]
                              }
                            }}
                            InputProps={{
                              style: { color: colors.grey[100] }
                            }}
                          />
                        </TableCell>
                        {tableHeaders.map((header, headerIdx) => (
                          <TableCell 
                            key={headerIdx}
                            sx={tableCellShrinkBase(1 + (tableHeaders?.length || tableCols || 1), {
                              border: `1px solid ${colors.greenAccent[700]}`,
                              textAlign: "center",
                              backgroundColor: colors.primary[600],
                              px: 0.5,
                              py: 0.5,
                            })}
                          >
                            <Radio 
                              disabled 
                              size="small"
                              sx={{
                                color: colors.greenAccent[600],
                                "&.Mui-disabled": {
                                  color: colors.greenAccent[700]
                                }
                              }}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ 
            p: 3, 
            borderTop: `2px solid ${colors.primary[300]}`,
            gap: 2
          }}>
            <Button 
              onClick={() => setTableModalOpen(false)}
              variant="outlined"
              sx={{
                borderColor: colors.redAccent[500],
                color: colors.redAccent[400],
                fontWeight: "bold",
                px: 3,
                "&:hover": {
                  borderColor: colors.redAccent[400],
                  backgroundColor: colors.redAccent[900],
                  transform: "scale(1.05)"
                },
                transition: "all 0.2s"
              }}
            >
              ✖ Cancel
            </Button>
            <Button 
              onClick={handleCreateTableQuestion}
              variant="contained"
              sx={{ 
                background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.blueAccent[600]} 100%)`,
                color: "white",
                fontWeight: "bold",
                px: 4,
                boxShadow: `0 4px 12px rgba(0, 0, 0, 0.3)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.blueAccent[700]} 100%)`,
                  transform: "scale(1.05)",
                  boxShadow: `0 6px 16px rgba(0, 0, 0, 0.4)`
                },
                transition: "all 0.2s"
              }}
            >
              ✓ Create Table Question
            </Button>
          </DialogActions>
        </Dialog>

        {/* Repeat Schedule Configuration Dialog */}
        {formInfo?.type === "for patients" && (
          <Dialog
            open={scheduleDialogOpen}
            onClose={() => setScheduleDialogOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                background: colors.primary[400],
                borderRadius: 3,
                boxShadow: `0 8px 32px rgba(0, 0, 0, 0.3)`,
              }
            }}
          >
            <DialogTitle sx={{ 
              borderBottom: `2px solid ${colors.blueAccent[400]}`,
              pb: 2
            }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 2,
                background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent"
              }}>
                <ScheduleIcon sx={{ 
                  fontSize: 32,
                  color: colors.blueAccent[400]
                }} />
                <Typography variant="h4" fontWeight="bold">
                  Configure Repeat Schedule
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {maxSentCount > 0 && (
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 2, 
                  backgroundColor: colors.primary[600],
                  border: `2px solid ${colors.grey[600]}`
                }}>
                  <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                    ⚠️ Note: {maxSentCount} send(s) have already been completed. You can only modify future send schedules.
                  </Typography>
                </Box>
              )}
              <RepeatSchedulePicker
                repeatCount={repeatCount}
                value={repeatSchedule}
                onChange={setRepeatSchedule}
                error={false}
                helperText=""
              />
            </DialogContent>
            <DialogActions sx={{ 
              p: 3, 
              borderTop: `2px solid ${colors.primary[300]}`,
              gap: 2
            }}>
              <Button 
                onClick={() => setScheduleDialogOpen(false)}
                variant="outlined"
                sx={{
                  borderColor: colors.greenAccent[500],
                  color: colors.greenAccent[400],
                  fontWeight: "bold",
                  px: 3,
                  "&:hover": {
                    borderColor: colors.greenAccent[400],
                    backgroundColor: colors.greenAccent[900],
                    transform: "scale(1.05)"
                  },
                  transition: "all 0.2s"
                }}
              >
                ✓ Done
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <ConfirmationDialog
          title={
            dialogType === "cancel"
              ? "Are you sure you want to cancel the transactions?"
              : "Are you sure you want to save the change?"
          }
          description={"If you click yes you won't go back"}
          open={dialogOpen}
          onClose={handleCloseDialog}
          onConfirm={dialogType === "cancel" ? handleCancel : handleSave}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{
              width: "100%",
              backgroundColor: snackbar.severity === "success" 
                ? colors.greenAccent[700] 
                : colors.redAccent[700],
              color: "white",
              fontWeight: 600,
              boxShadow: `0 4px 12px ${
                snackbar.severity === "success" 
                  ? colors.greenAccent[500] 
                  : colors.redAccent[500]
              }40`,
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
